import { inject, Injectable } from '@angular/core';

import { Actions } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { mergeMap, Observable, of, switchMap, take } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

import { filterBoolean } from '@core/common-legacy';
import { createEffectFactory } from '@core/ngrx-legacy';
import { ReportDashboardComponentKind } from '@core/reports-types';

import { ReportsResultsHttpClient } from './results.http-client';
import { ReportsResultsActions } from './results.store';
import { ReportsMetricsSelectors } from '../metrics/metrics.store';

@Injectable()
export class ReportsResultsEffects {
  private readonly actions$ = inject(Actions);
  private readonly http = inject(ReportsResultsHttpClient);
  private readonly store = inject(Store);

  public effects = createEffectFactory(ReportsResultsActions, this.actions$);

  public fetchVisualization$ = this.effects(
    ReportsResultsActions.fetchOneResult,
    ({ payload: { paramsHash, params } }) =>
      this.getMetricKind(params.metric).pipe(
        switchMap(kind =>
          this.http.load(kind, params).pipe(
            map(response =>
              ReportsResultsActions.fetchOneResultSuccess({
                payload: {
                  paramsHash,
                  response,
                },
              }),
            ),
            catchError(err =>
              of(
                ReportsResultsActions.fetchOneResultFailed({
                  payload: {
                    paramsHash: paramsHash,
                    error: {
                      status: err.status,
                      message: err.message,
                    },
                  },
                }),
              ),
            ),
          ),
        ),
      ),
    { customMapOperator: mergeMap },
  );

  private getMetricKind = (metric: string): Observable<ReportDashboardComponentKind> =>
    this.store.select(ReportsMetricsSelectors.byId(metric)).pipe(
      filterBoolean,
      map(metric => metric.kind),
      take(1),
    );
}
